
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { entityConfig, removeAllEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import EstensaModel, { ImagingOpenParams } from 'estensa-module';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, filter, of, pipe, switchMap, tap } from 'rxjs';
import { BrokerExamIdsAndCustomPageType } from './broker.models';
import {
    checkDifferentData,
    checkModifiedStudies,
    checkPresExams,
    checkSuspectStudies,
    convertStudiesToPacsData,
} from './broker.helper';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BrokerService } from '../services/angularjs/broker.service';
import { ModalDialogNotificationService } from '../services/notification.service';
import { RouterStackService } from '../utils/routing/router-stack.service';
import { ImagingService } from '../services/imaging/imaging.service';
import { PacsStudyDataVnext, fromNg1PacsStudyData } from '../services/imaging/common';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { EstensaAuthorizationServiceService } from '../services/angularjs/estensa-authorization-service.service';
import { BrokerConfirmDialogComponent } from './modals/broker-confirm-dialog/broker-confirm-dialog.component';
import { BrokerAssosiationModalComponent } from './modals/broker-association-modal/broker-association-modal.component';
import { CustomPageType } from '@se/platform';
import { IRadioEprEventEx } from '../models/patient-history.models';

const studyConfig = entityConfig({
    entity: type<EstensaModel.BrokerDataStudy>(),
    collection: 'study',
    selectId: (study) => study.studyUID,
});

const examConfig = entityConfig({
    entity: type<EstensaModel.BrokerDataExam>(),
    collection: 'exam',
    selectId: (exam) => exam.idExam,
});

export type BrokerDataDisplaySignalStore = {
    suspectedStudies: EstensaModel.BrokerDataDisplay[] | null;
    modifiedStudies: EstensaModel.BrokerDataDisplay[] | null;
    differentData: EstensaModel.BrokerDataDisplay[] | null;
    pirata: EstensaModel.IBrokerDataDisplay[] | null;
    brokerParams: EstensaModel.BrokerCheckParams;
};

const initialState: BrokerDataDisplaySignalStore = {
    suspectedStudies: null,
    modifiedStudies: null,
    differentData: null,
    pirata: null,
    brokerParams: undefined!,
};

export const BrokerSignalStore = signalStore(
    { providedIn: 'root' },
    withEntities(studyConfig),
    withEntities(examConfig),
    withState<BrokerDataDisplaySignalStore>(initialState),
    withComputed((_entities) => ({
        // 👇 exposing entity array publicly
        exams: _entities.examEntities,
        studies: _entities.studyEntities,
    })),
    //ENTINTY METHODS
    withMethods((store) => ({
        addStudies(studies: EstensaModel.BrokerDataStudy[]): void {
            patchState(store, removeAllEntities({ collection: 'study' }));
            patchState(store, setAllEntities(studies, studyConfig));
        },
        addExams(exams: EstensaModel.BrokerDataExam[]): void {
            patchState(store, setAllEntities(exams, examConfig));
        },
        setCurrentAssociation(pirata: EstensaModel.IBrokerDataDisplay[]): void {
            patchState(store, { pirata });
        },

        setCurrentBrokerCheckParams(brokerCheckParams: EstensaModel.BrokerCheckParams): void {
            return patchState(store, { brokerParams: brokerCheckParams });
        },

        addSuspectedStudies(studies: EstensaModel.BrokerDataDisplay[]): void {
            return patchState(store, {
                suspectedStudies: studies,
                //modifiedStudies: null,
                //differentData: null,
                //pirata: null,
            });
        },

        addModifiedStudies(studies: EstensaModel.BrokerDataDisplay[]): void {
            return patchState(store, {
                //suspectedStudies: null,
                modifiedStudies: studies,
                //differentData: null,
                //pirata: null,
            });
        },

        addDifferentStudies(studies: EstensaModel.BrokerDataDisplay[]): void {
            return patchState(store, {
                //suspectedStudies: null,
                //modifiedStudies: null,
                differentData: studies,
                //pirata: null,
            });
        },

        addPirata(pirata: EstensaModel.IBrokerDataDisplay[]): void {
            return patchState(store, {
                //suspectedStudies: null,
                //modifiedStudies: null,
                //differentData: null,
                pirata,
            });
        },
        resetStore() {
            patchState(store, removeAllEntities({ collection: 'study' }));
            patchState(store, removeAllEntities({ collection: 'exam' }));
            patchState(store, {
                brokerParams: undefined!,
                pirata: null,
                suspectedStudies: null,
                modifiedStudies: null,
                differentData: null,
            });
        },
    })),
    withMethods((store, imagingService = inject(ImagingService)) => ({
        /*
         * Open broker from External source
         */
        openBrokerFromExternal: rxMethod<BrokerExamIdsAndCustomPageType>(
            pipe(
                switchMap((brokerParams) => {
                    imagingService.activateViewerByExaminations(brokerParams.customPageType);
                    return of(brokerParams);
                }),
                tap((brokerParams: BrokerExamIdsAndCustomPageType) => {

                    const params = new EstensaModel.BrokerCheckParams(
                        brokerParams.examinationIds,
                        null, // idIs
                        false, // isLocked
                        true, // isInteractive
                        {
                            /**
                             * Image loading options 📸
                             */
                            appendToPreviousImages: false,
                            discardPreviousImages: true,
                            useAlternativeUrl: false,
                        },
                    );
                    /**
                     * Set the current broker check parameters in the store 📈
                     */
                    store.setCurrentBrokerCheckParams(params);
                }),
            ),
        ),
    })),
    //METHODS
    withMethods((store, dialogService = inject(MatDialog)) => ({
        /**
         * Opens a modal dialog for reconciling studies and handles the user's response.
         * If the user cancels, resets the store.
         */
        openReconcilePopUp: rxMethod<boolean>(
            pipe(
                switchMap((findStudyAssoc) => {
                    const WIDTH = '100vw';
                    const HEIGHT = '100vh';

                    const config = new MatDialogConfig();
                    config.disableClose = true;
                    config.minWidth = WIDTH;
                    config.height = HEIGHT;
                    config.minHeight = HEIGHT;
                    config.data = {
                        origin: {
                            params: store.brokerParams(),
                            studies: store.studies(),
                            exams: store.exams(),
                        },
                        findStudyAssoc: findStudyAssoc,
                    };

                    const dialogRef = dialogService.open(BrokerAssosiationModalComponent, config);
                    return dialogRef.afterClosed().pipe(
                        switchMap((data) => {
                            if (data && data.action == 'Cancel') {
                                store.resetStore();
                            }
                            return EMPTY;
                        }),
                    );
                }),
            ),
        ),
    })),
    withMethods((store, imagingService = inject(ImagingService)) => ({
        /**
         * Opens the image viewer with the selected studies and resets the store.
         * Converts the studies to PACS data, maps to Ng1 PACS study data, and passes to the imaging service.
         */
        onOpenViewer: rxMethod<EstensaModel.BrokerDataStudy[]>(
            pipe(
                switchMap((studies) => {
                    // Convert studies to PACS data and prepare for image viewer
                    const searchParams: EstensaModel.BrokerCheckParams = store.brokerParams();
                    const pacsStudyDataList: EstensaModel.PacsStudyData[] = convertStudiesToPacsData(studies);
                    const list = pacsStudyDataList.map((d) => {
                        return fromNg1PacsStudyData(d);
                    });

                    //store.resetStore();
                    imagingService.viewImages(list, searchParams.credentials, searchParams.openParams);
                    store.resetStore();
                    return EMPTY;
                }),
            ),
        ),
    })),
    withMethods(
        (
            store,
            dialogService = inject(MatDialog),
            authorizationService = inject(EstensaAuthorizationServiceService),
        ) => ({
            /**
             * Opens a confirmation dialog and handles the user's response.
             * Based on the response, performs different actions such as opening another dialog, adding studies to the store, or resetting the store.
             * When the dialog is closed, it checks the returned data and performs different actions based on the action type and the presence of certain studies in the store:
                    ✔️ Resets the store if the action is 'Cancel'
                    ✔️ Opens the openReconcilePopUp method or
                    ✔️ adds studies to the store if the action is 'Yes', 'No', or 'Ok' and certain conditions are met
             */
            openPopUpRX: rxMethod<{
                title: string;
                message: string;
                hasOk: boolean;
                hasCancel: boolean;
                hasYes: boolean;
                hasNo: boolean;
            }>(
                pipe(
                    switchMap((ref) => {
                        // Open a confirmation dialog with a custom message
                        const WIDTH = '900px';
                        const HEIGHT = '500px';
                        const config = new MatDialogConfig();
                        config.disableClose = true;
                        config.width = WIDTH;
                        config.height = HEIGHT;
                        config.maxWidth = WIDTH;
                        config.maxHeight = HEIGHT;
                        config.minWidth = WIDTH;
                        config.minHeight = HEIGHT;
                        config.data = {
                            origin: {
                                suspectedStudies: store.suspectedStudies() ? store.suspectedStudies() : [],
                                modifiedStudies: store.modifiedStudies() ? store.modifiedStudies() : [],
                                diferentData: store.differentData() ? store.differentData() : [],
                                pirata: store.pirata() ? store.pirata() : [],
                            },
                            hasOk: ref.hasOk,
                            hasCancel: ref.hasCancel,
                            hasYes: ref.hasYes,
                            hasNo: ref.hasNo,
                            title: ref.title,
                            message: ref.message,
                        };
                        const dialogRef = dialogService.open(BrokerConfirmDialogComponent, config);
                        return dialogRef.afterClosed().pipe(
                            switchMap((data) => {
                                // Handle user response
                                if (data && data.action == 'Cancel') {
                                    // Reset the store if the user cancels
                                    store.resetStore();
                                    return EMPTY;
                                }
                                //B_YES_SUSPECT
                                // Handle 'Yes' response for suspected studies
                                if (data && data.action == 'Yes' && store.suspectedStudies()) {
                                    if (
                                        authorizationService.isAuthorized(
                                            [authorizationService.permissions.broker_BrokerOnDemandAccess],
                                            'single',
                                        )
                                    ) {
                                        // Open the reconcile popup if the user has the permission
                                        store.openReconcilePopUp(true);
                                        return EMPTY;
                                    } else {
                                        // Add studies to the store if the user does not have the permission
                                        const studies = store.studies().filter((item) => {
                                            return item.suspect != true;
                                        });
                                        store.addStudies(studies);
                                        return EMPTY;
                                    }
                                }
                                // Handle other responses for suspected studies
                                else if (data && data.action == 'No' && store.suspectedStudies()) {
                                    const studies = store.studies().filter((item) => {
                                        return item.suspect != true;
                                    });
                                    if (studies.length > 0) {
                                        store.addStudies(studies);
                                    }
                                    store.resetStore();
                                    return EMPTY;
                                }
                                //B_YES_MODIFIED
                                //B_YES_DIFFERENT
                                //B_YES_PIRATA
                                // Handle 'Yes' response for non-suspected studies
                                else if (data && data.action == 'Yes' && !store.suspectedStudies()) {
                                    store.openReconcilePopUp(false);
                                    return EMPTY;
                                }
                                // Handle other responses for non-suspected studies
                                else if (data && data.action == 'No' && !store.suspectedStudies()) {
                                    store.resetStore();
                                    return EMPTY;
                                } else if (data && data.action == 'Ok') {
                                    //store.onOpenViewer(store.studies());
                                    const studies = store.studies();
                                    store.addStudies(studies);
                                    return EMPTY;
                                }
                                // Default action: reset the store
                                store.resetStore();
                                return EMPTY;
                            }),
                        );
                    }),
                ),
            ),
        }),
    ),
    withMethods((store, brokerService = inject(BrokerService), translate = inject(TranslateService)) => ({
        /**
         * Retrieves associations on PIR display and handles the response.
         * If associations are found, opens a popup to update the anagrafica.
         * If no associations are found, opens the image viewer.
         */
        getAssociationsOnPIRDisplay: rxMethod<void>(
            pipe(
                switchMap(() => {
                    // Initialize an empty array to store exams with PIR display
                    let idPresExams = new Array<EstensaModel.BrokerDataExam>;

                    // Iterate through the ID pres list and filter exams with PIR display
                    store.brokerParams().idPresList.forEach((idPres: string) => {
                        let currentExams: EstensaModel.BrokerDataExam[] = store.exams().filter((exam) => {
                            return exam.idExam == idPres && exam.piRata == true;
                        });
                        // Add exams with PIR display to the array
                        currentExams.forEach((exam: EstensaModel.BrokerDataExam) => {
                            idPresExams.push(exam);
                        });
                    });
                    // Check if any exams with PIR display were found
                    if (idPresExams.length > 0) {
                        // Call the broker service to retrieve associations on PIR display
                        return brokerService.getAssociationsOnPIRDisplay(idPresExams).pipe(
                            tap((response) => {
                                if (response && response.success) {
                                    if (response.data && response.data.length == 0) {
                                        // If no associations were found, open the image viewer
                                        return store.onOpenViewer(store.studies());
                                    } else {
                                        // If associations were found, add them to the store and open a popup to update the anagrafica
                                        store.addPirata(response.data as EstensaModel.IBrokerDataDisplay[]);
                                        const title = 'Broker';
                                        const message = translate.instant('PrevUpdateAnagraficaV2');
                                        return store.openPopUpRX({
                                            title: title,
                                            message: message,
                                            hasOk: true,
                                            hasCancel: true,
                                            hasYes: false,
                                            hasNo: false,
                                        });
                                    }
                                } else {
                                    // If the response was not successful, open the image viewer
                                    return store.onOpenViewer(store.studies());
                                }
                            }),
                        );
                    } else {
                        // If no exams with PIR display were found, open the image viewer
                        store.onOpenViewer(store.studies());
                        return EMPTY;
                    }
                }),
            ),
        ),
    })),
    //METHODS TO USE IN COMPONENT
    withMethods(
        (
            store,
            modalDialogNotificationService = inject(ModalDialogNotificationService),
            translate = inject(TranslateService),
            routerStackService = inject(RouterStackService),
            authorizationService = inject(EstensaAuthorizationServiceService),
            radioSettings = inject(RadioSettingsService),
        ) => ({
            checkStudiesRedirectionToCollaborate() {
                const pacsStudyDataList: EstensaModel.PacsStudyData[] = convertStudiesToPacsData(store.studies());

                if (pacsStudyDataList.length === 0) {
                    modalDialogNotificationService.warning(
                        translate.instant('NoStudyAssociated'),
                        translate.instant('Warning'),
                    );
                } else {
                    const queryParams = { pacsStudyDataList: pacsStudyDataList };
                    routerStackService.go('/new/vnext/collaboration/teams', queryParams);
                }
            },

            /**
             * 📣 onCheckStudiesToOpenPopUp: This function checks studies and opens a popup based on certain conditions 📣
             *
             * @param studies: EstensaModel.BrokerDataStudy[] - array of studies to check 📊
             */

            onCheckStudiesToOpenPopUp: rxMethod<EstensaModel.BrokerDataStudy[]>(
                pipe(
                    // 🔍 Filter out empty arrays of studies
                    filter((studies) => studies.length > 0),
                    // 🔀 Switch to a new observable based on the filtered studies
                    switchMap((studies) => {
                        // 🚫 If brokerParams is locked or not interactive, return an empty observable
                        if (store.brokerParams().isLocked || store.brokerParams().isInteractive == false) return EMPTY;
                        let hasYes = false;
                        let hasNo = false;
                        let hasCancel = false;
                        let hasOk = false;

                        // 👀 Check for suspected studies 🕵️‍♀️
                        if (!store.suspectedStudies()?.length) {
                            const suspectedStudies: EstensaModel.BrokerDataDisplay[] = checkSuspectStudies(
                                store.brokerParams(),
                                studies,
                                store.exams(),
                            );
                            const onCheckSuspectedStudies: boolean = suspectedStudies.length === 0 ? false : true;
                            if (onCheckSuspectedStudies === true) {
                                // 📝 Add suspected studies to store
                                store.addSuspectedStudies(suspectedStudies);

                                // 📣 Open popup with suspected studies message
                                let title = translate.instant('ImageArchive');
                                let message: string;
                                let iSuspectStudies: number = store.studies().filter((item) => {
                                    return item.suspect;
                                }).length;
                                if (iSuspectStudies > 0) {
                                    message = translate.instant('SuspectStudiesMessage', {
                                        p0: iSuspectStudies,
                                    });
                                } else {
                                    message = translate.instant('SuspectStudyMessage');
                                }

                                // 🔒 Check if user has necessary permissions
                                if (
                                    authorizationService.isAuthorized(
                                        [authorizationService?.permissions.broker_BrokerOnDemandAccess],
                                        'single',
                                    )
                                ) {
                                    // 🤔 Add popup buttons based on permissions
                                    if (iSuspectStudies > 0) {
                                        message += '\n\r' + translate.instant('WantToValidateMulti');
                                    } else {
                                        message += '\n\r' + translate.instant('WantToValidate');
                                    }
                                    hasYes = true;
                                    hasNo = true;
                                    hasCancel = true;
                                } else {
                                    message += '\n\r' + translate.instant('UserNotGranted');
                                    hasOk = true;
                                }

                                // 📣 Open popup with message and buttons
                                store.openPopUpRX({
                                    title: title,
                                    message: message,
                                    hasOk: hasOk,
                                    hasCancel: hasCancel,
                                    hasYes: hasYes,
                                    hasNo: hasNo,
                                });

                                return EMPTY;
                            }
                        }

                        // 👀 Check for modified studies 📝
                        if (!store.modifiedStudies()?.length) {
                            const modifiedStudies: EstensaModel.BrokerDataDisplay[] = checkModifiedStudies(
                                store.brokerParams(),
                                studies,
                                store.exams(),
                            );
                            const onCheckModifiedStudies: boolean = modifiedStudies.length === 0 ? false : true;
                            if (onCheckModifiedStudies === true) {
                                // 📝 Add modified studies to store
                                store.addModifiedStudies(modifiedStudies);

                                // 📣 Open popup with modified studies message
                                let title = 'Broker'; //this.$translate.instant('ImageArchive');
                                let message = translate.instant('ModifiedStudiesMessage');
                                hasOk = true;
                                hasCancel = true;
                                store.openPopUpRX({
                                    title: title,
                                    message: message,
                                    hasOk: hasOk,
                                    hasCancel: hasCancel,
                                    hasYes: hasYes,
                                    hasNo: hasNo,
                                });
                                return EMPTY;
                            }
                        }

                        // 👀 Check for different data 📊
                        if (!store.differentData()?.length) {
                            const radioSetting = radioSettings?.radioSettings?.brokerSettings
                                ? radioSettings.radioSettings.brokerSettings
                                : undefined;
                            const differentData: EstensaModel.BrokerDataDisplay[] | undefined = checkDifferentData(
                                store.brokerParams(),
                                studies,
                                store.exams(),
                                radioSetting,
                            );
                            const onCheckDifferentData: boolean =
                                differentData && differentData.length === 0 ? false : true;
                            if (differentData && onCheckDifferentData && onCheckDifferentData === true) {
                                // 📝 Add different data to store
                                store.addDifferentStudies(differentData);

                                // 📣 Open popup with different data message
                                let title = 'Broker';
                                let message = translate.instant('DifferentStudiesMessage');
                                hasOk = true;
                                hasCancel = true;
                                store.openPopUpRX({
                                    title: title,
                                    message: message,
                                    hasOk: hasOk,
                                    hasCancel: hasCancel,
                                    hasYes: hasYes,
                                    hasNo: hasNo,
                                });
                                return EMPTY;
                            }
                        }

                        // 👀 Check for pirata studies 🏴‍☠️
                        if (!store.pirata()?.length) {
                            const pirSettingsEnableNotification: boolean = radioSettings?.radioSettings?.brokerSettings
                                .pirSettingsEnableNotification
                                ? true
                                : false;
                            if (pirSettingsEnableNotification === true) {
                                // 📝 Get associations on PIR display
                                store.getAssociationsOnPIRDisplay();
                                return EMPTY;
                            }
                        }


                        // 📊 Open viewer with current studies
                        store.onOpenViewer(store.studies());
                        return EMPTY;
                    }),
                ),
            ),
        }),
    ),

    //API METHODS
    withMethods(
        (
            store,
            brokerService = inject(BrokerService),
            translate = inject(TranslateService),
            authorizationService = inject(EstensaAuthorizationServiceService), //UserConfigurationSettingsService
        ) => ({
            loadExams: rxMethod<EstensaModel.BrokerCheckParams>(
                pipe(
                    switchMap((searchParams) => {
                        if (!searchParams) {
                            patchState(store, removeAllEntities({ collection: 'exam' }));
                            return EMPTY;
                        }
                        //check pres
                        let filterExams = new EstensaModel.BrokerDataFilterExam();
                        filterExams.setSearchByIdPresList(searchParams, EstensaModel.BrokerTypeFilter.All);
                        return brokerService.findExams(filterExams).pipe(
                            tap((response) => {
                                if (response.success) {
                                    if (response.data && response.data.length > 0) {
                                        const check: boolean = checkPresExams(searchParams, response.data);
                                        if (check) return store.addExams(response.data);
                                        //TODO RESPONSE ON CHECK FALSE
                                    } else if (response.data && response.data.length == 0) {
                                        //B_NO_EXAMS
                                        store.resetStore();
                                        return store.openPopUpRX({
                                            title: translate.instant('ImageArchive'),
                                            message: translate.instant('ExamsNotInBroker'),
                                            hasOk: true,
                                            hasCancel: false,
                                            hasYes: false,
                                            hasNo: false,
                                        });
                                    }
                                }
                            }),
                        );
                    }),
                ),
            ),

            loadStudies: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        const searchParams: EstensaModel.BrokerCheckParams = store.brokerParams();
                        let filterStudies = new EstensaModel.BrokerDataFilterStudy();
                        filterStudies.setSearchByIdPresList(searchParams, EstensaModel.BrokerTypeFilter.TypeAscToPre);
                        // studi associati alle prestazioni in params
                        return brokerService._findStudies(filterStudies).pipe(
                            tap((response) => {
                                if (response.success && response.data && response.data.length > 0) {
                                    return store.addStudies(response.data);
                                }
                            }),
                        );
                    }),
                ),
            ),

            /**
             * 📊 loadStudiesFromExams: This function loads studies from exams 📊
             *
             * @param exams: EstensaModel.BrokerDataExam[] - array of exams to load studies from 📊
             */
            loadStudiesFromExams: rxMethod<EstensaModel.BrokerDataExam[]>(
                pipe(
                    //filter((exams) => !!exams.length),
                    switchMap((exams) => {
                        if (!exams.length) {
                            // 🚫 If there are no exams, remove all entities and return an empty observable
                            patchState(store, removeAllEntities({ collection: 'study' }));
                            //patchState(store, removeAllEntities({ collection: 'exam' }));
                            patchState(store, { brokerParams: undefined! });
                            return EMPTY;
                        }
                        // 🔍 Get search parameters from store
                        const searchParams: EstensaModel.BrokerCheckParams = store.brokerParams();

                        // 🔍 Check if exams are present in search parameters
                        const onCheckPressExams: boolean = searchParams ? checkPresExams(searchParams, exams) : false;

                        // 🔍 If exams are present, filter studies
                        if (onCheckPressExams) {
                            let filterStudies = new EstensaModel.BrokerDataFilterStudy();
                            filterStudies.setSearchByIdPresList(
                                searchParams,
                                EstensaModel.BrokerTypeFilter.TypeAscToPre,
                            );
                            // studi associati alle prestazioni in params
                            // 🔍 Find studies using broker service
                            return brokerService._findStudies(filterStudies).pipe(
                                tap((response) => {
                                    if (response.success && response.data) {
                                        let message: string = '';
                                        let hasYes = false;
                                        let hasNo = false;
                                        let hasCancel = false;
                                        let hasOk = false;

                                        // 🔍 If no studies are found, display message
                                        if (response.data.length == 0) {
                                            if (searchParams.isLocked) {
                                                //B_LOCKED
                                                // 🔒 Locked, cannot associate studies
                                                return store.openPopUpRX({
                                                    title: translate.instant('ImageArchive'),
                                                    message:
                                                        translate.instant('NoAssociatedStudies') +
                                                        '\n\r' +
                                                        translate.instant('LockedCannotAssociate'),
                                                    hasOk: true,
                                                    hasCancel: false,
                                                    hasYes: false,
                                                    hasNo: false,
                                                });
                                            } else if (searchParams.isInteractive == false) {
                                                //B_NO_STUDIES_NO_INT
                                                // 🔒 No interactive, no studies
                                                return store.openPopUpRX({
                                                    title: translate.instant('ImageArchive'),
                                                    message: translate.instant('NoAssociatedStudies'),
                                                    hasOk: true,
                                                    hasCancel: false,
                                                    hasYes: false,
                                                    hasNo: false,
                                                });
                                            } else {
                                                //B_NO_STUDIES
                                                // 🔓 No studies
                                                let title: string = translate.instant('ImageArchive');

                                                if (
                                                    authorizationService.isAuthorized(
                                                        [authorizationService.permissions.broker_BrokerOnDemandAccess],
                                                        'single',
                                                    )
                                                ) {
                                                    message =
                                                        translate.instant('NoAssociatedStudies') +
                                                        '\n\r' +
                                                        translate.instant('WantToAssociate');
                                                    hasYes = true;
                                                    hasNo = true;
                                                } else {
                                                    message =
                                                        translate.instant('NoAssociatedStudies') +
                                                        '\n\r' +
                                                        translate.instant('UserNotGranted');
                                                    hasOk = true;
                                                }

                                                return store.openPopUpRX({
                                                    title: title,
                                                    message: message,
                                                    hasOk: hasOk,
                                                    hasCancel: hasCancel,
                                                    hasYes: hasYes,
                                                    hasNo: hasNo,
                                                });
                                            }
                                        } else {
                                            //B_EXIT
                                            // 🔓 Studies found, process data
                                            if (searchParams.isLocked || searchParams.isInteractive == false) {

                                                store.onOpenViewer(response.data);
                                            }
                                            //B_YES_STUDIES
                                            // 🔓 Yes, studies found
                                            store.addStudies(response.data);
                                            return EMPTY;
                                        }
                                    }
                                }),
                            );
                        }
                        return EMPTY;
                    }),
                ),
            ),
        }),
    ),

    //Broker CHECK

    withMethods(
        (
            store,
            radioSettings = inject(RadioSettingsService),
            imagingService = inject(ImagingService),
            modalDialogNotificationService = inject(ModalDialogNotificationService),
            translate = inject(TranslateService),
        ) => ({
            openImagesByEprEvent: rxMethod<{
                sourceOpenerType: CustomPageType;
                items: IRadioEprEventEx[];
                credentials: EstensaModel.AuthenticationData;
                openParams: ImagingOpenParams;
            }>(
                pipe(
                    switchMap((params) => {
                        imagingService.activateViewerByExaminations(params.sourceOpenerType);


                        // 🔍 Check if broker verification is enabled in radio settings
                        const checkRadioSettingsOnBroker =
                            radioSettings.radioSettings?.viewerSettings.brokerCheck == true;
                        const credentials = params.credentials;
                        const openParams = params.openParams;
                        const eprEvents = params.items;
                        let pacsStudyDataList: PacsStudyDataVnext[] = [];

                        // ==============================================
                        // CASE 1: Broker verification is NOT required 🚫
                        // ==============================================
                        if (!checkRadioSettingsOnBroker) {
                            // 🏗️ Build PACS study data directly without broker interaction
                            pacsStudyDataList = eprEvents.map((eprEvent: IRadioEprEventEx) => {
                                return {
                                    patient: {
                                        patientId: eprEvent.patient.id,
                                        fullName: eprEvent.patient.firstName + '^' + eprEvent.patient.lastName,
                                        sex: eprEvent.patient.sex.code,
                                        birthDate: eprEvent.patient.birthDate,
                                    },
                                    study: {
                                        studyInstanceUID:
                                            eprEvent.pacsStudyData != null && eprEvent.pacsStudyData.length > 0
                                                ? eprEvent.pacsStudyData[0].studyInstanceUID
                                                : undefined,
                                        studyDate: eprEvent.dateString,
                                        accessionNumber: eprEvent.accessionNumber,
                                        modality: undefined,
                                    },
                                };
                            });
                            // 🖼️ Open images directly using imaging service
                            imagingService.viewImages(pacsStudyDataList, credentials, openParams);
                            return EMPTY;
                        }
                        // ==============================================
                        // CASE 2: Broker verification IS required ✅
                        // ==============================================
                        else {
                            let idIs: number | null = null;
                            if (eprEvents[0].idIs != null) {
                                idIs = parseInt(eprEvents[0].idIs);
                            }
                            const isInteractive: boolean = false;

                            // 🔄 Handle INTERNAL events (non-external)
                            if (eprEvents[0].isExternal === false) {
                                // 📋 Extract all event IDs for broker check
                                const eventIdList = eprEvents
                                    .filter((evt) => evt.eventId != null)
                                    .map((evt) => evt.eventId);

                                const brokerCheckParams: EstensaModel.BrokerCheckParams = {
                                    idPresList: eventIdList,
                                    idIs: idIs ?? undefined,
                                    isLocked: false,
                                    isInteractive: isInteractive,
                                    openParams: openParams,
                                    credentials: credentials,
                                };
                                // 💾 Store params for broker verification
                                store.setCurrentBrokerCheckParams(brokerCheckParams);
                                return EMPTY; // ⏸️ Pause flow until broker verification completes
                            }
                            // 🔄 Handle EXTERNAL events with ID_IS
                            else if (idIs != null) {
                                // Check if there are valid idPres values
                                if (
                                    eprEvents[0].idPres != null &&
                                    eprEvents.filter((evt) => {
                                        return evt.idPres && evt.idPres.length > 0;
                                    }).length > 0
                                ) {
                                    // 📋 Extract all idPres values for broker check
                                    const idList = eprEvents
                                        .filter((evt) => evt.idPres && evt.idPres.length > 0)
                                        .flatMap((evt) => evt.idPres);

                                    const brokerCheckParams: EstensaModel.BrokerCheckParams = {
                                        idPresList: idList,
                                        idIs: idIs,
                                        isLocked: false,
                                        isInteractive: isInteractive,
                                        openParams: openParams,
                                        credentials: credentials,
                                    };

                                    // 💾 Store params for broker verification
                                    store.setCurrentBrokerCheckParams(brokerCheckParams);
                                    return EMPTY; // ⏸️ Pause flow until broker verification completes
                                }
                                // 🆕 Handle external sources without idPres (fallback to direct PACS access)
                                else {
                                    // 🏗️ Build PACS study data using studyInstanceUID as fallback
                                    pacsStudyDataList = eprEvents.map((eprEvent) => {
                                        return {
                                            patient: {
                                                patientId: eprEvent.patient.id,
                                                fullName: eprEvent.patient.firstName + '^' + eprEvent.patient.lastName,
                                                sex: eprEvent.patient.sex.description,
                                                birthDate: eprEvent.patient.birthDate,
                                            },
                                            study: {
                                                studyInstanceUID:
                                                    eprEvent.pacsStudyData != null && eprEvent.pacsStudyData.length > 0
                                                        ? eprEvent.pacsStudyData[0].studyInstanceUID
                                                        : undefined,
                                                studyDate: eprEvent.dateString,
                                                accessionNumber: eprEvent.accessionNumber,
                                                modality: undefined,
                                            },
                                        };
                                    });
                                    // 🖼️ Open images directly using imaging service
                                    imagingService.viewImages(pacsStudyDataList, credentials, openParams);
                                    return EMPTY;
                                }
                            }
                            // ❌ Invalid data case
                            else {
                                modalDialogNotificationService.warning(
                                    translate.instant('InvalidData'),
                                    translate.instant('InvalidData'),
                                );
                                return EMPTY;
                            }
                        }
                    }),
                ),
            ),
        }),
    ),
);
